import React from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { LoadingSkeleton } from "@app.automotus.io/components/LoadingSkeleton";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import Box from "@mui/material/Box";
import Popover from "@mui/material/Popover";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import LoadingButton from "@mui/lab/LoadingButton";
import { Formik, Form, FormikHelpers, FormikProps, FormikValues } from "formik";
import useSnackPackContext from "@app.automotus.io/components/context/SnackPack/useSnackPackContext";
import * as Yup from "yup";

export function SessionDetailsItem<FormValues>({
  loading,
  title,
  content,
  skeletonWidth,
  initialValues,
  validationSchema,
  onSubmitEdit,
  children,
}: SessionDetailsItemProps<FormValues>): JSX.Element {
  const anchorRef = React.useRef<HTMLElement>(null);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const { publishSnackbarMessage } = useSnackPackContext();

  const handleClickEdit = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseEditDialog = () => {
    setAnchorEl(null);
  };

  const handleSubmit = async (
    values: FormikValues & FormValues,
    { setSubmitting }: FormikHelpers<FormikValues & FormValues>,
  ) => {
    try {
      if (onSubmitEdit) {
        await onSubmitEdit(values);

        // Show success message
        publishSnackbarMessage({
          severity: "success",
          message: `${title} successfully updated`,
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        });
      }
      handleCloseEditDialog();
    } catch (error) {
      console.error("Error submitting edit:", error);

      // Show error message
      publishSnackbarMessage({
        severity: "error",
        message: `Failed to update ${title.toLowerCase()}. Please try again.`,
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Grid item xs={3}>
      <Typography variant={"subtitle1"} fontWeight={500}>
        {title}
      </Typography>
      <LoadingSkeleton loading={loading} width={skeletonWidth}>
        <Box ref={anchorRef} display={"flex"} alignItems={"center"} gap={1}>
          <Typography variant={"body1"}>{content || "_"}</Typography>
          {onSubmitEdit && (
            <IconButton size="small" onClick={handleClickEdit}>
              <EditIcon sx={{ fontSize: "16px" }} />
            </IconButton>
          )}
        </Box>
        {onSubmitEdit && (
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleCloseEditDialog}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          >
            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
              {({ isSubmitting, ...rest }) => (
                <Form>
                  <Stack spacing={2} p={2}>
                    <Box>
                      {children && (typeof children === "function" ? children({ isSubmitting, ...rest }) : children)}
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                      <LoadingButton
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={isSubmitting}
                        loading={isSubmitting}
                        fullWidth
                      >
                        Save
                      </LoadingButton>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={handleCloseEditDialog}
                        disabled={isSubmitting}
                        fullWidth
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Stack>
                </Form>
              )}
            </Formik>
          </Popover>
        )}
      </LoadingSkeleton>
    </Grid>
  );
}

export type SessionDetailsItemProps<FormValues> =
  | StaticSessionDetailsItemProps
  | EditableSessionDetailsItemProps<FormValues>;

export interface SessionDetailsItemBaseProps {
  loading?: boolean;
  title: string;
  content?: string;
  skeletonWidth?: number;
  skeletonHeight?: number;
}

export interface StaticSessionDetailsItemProps extends SessionDetailsItemBaseProps {
  initialValues?: never;
  validationSchema?: never;
  onSubmitEdit?: never;
  children?: never;
}

export interface EditableSessionDetailsItemProps<FormValues> extends SessionDetailsItemBaseProps {
  /**
   * Optional callback function to handle the submission of an edit. The edit
   * button will only be shown if this function is provided.
   */
  initialValues: FormikValues & FormValues;
  validationSchema?: Yup.AnyObjectSchema;
  onSubmitEdit?: (values: FormValues) => Promise<void>;
  children?: ((props: FormikProps<FormikValues & FormValues>) => React.ReactNode) | React.ReactNode;
}

export default SessionDetailsItem;
